import { Image } from '../image'
import { buildImageCandidates } from './imageCandidates'
import type { TResponsiveImageProps } from './ResponsiveImage.types'

/**
 * What `next/image` does in audio-web, without the framework. The fallback `src` is the largest width,
 * because a client that ignores `srcSet` still has to get a usable image.
 */
export const ResponsiveImage = ({
    src,
    sources,
    resize,
    priority = false,
    ...props
}: TResponsiveImageProps) => {
    const fixedWidth = props.sizes ? undefined : Number(props.width) || undefined
    const candidates = buildImageCandidates({
        src,
        resize,
        width: fixedWidth,
        sizes: props.sizes,
    })

    const image = (
        <Image
            {...props}
            src={candidates.src}
            srcSet={candidates.srcSet}
            // A priority image carries no `loading` at all, as `next/image` emits it — `eager` is
            // the same behaviour but a different attribute, and the crawler reads the attribute.
            loading={priority ? undefined : 'lazy'}
            fetchPriority={priority ? 'high' : undefined}
            decoding="async"
        />
    )

    if (!sources?.length) {
        return image
    }

    return (
        <picture>
            {sources.map(source => (
                <source
                    key={`${source.media ?? ''}${source.type ?? ''}${source.srcSet}`}
                    srcSet={source.srcSet}
                    media={source.media}
                    type={source.type}
                />
            ))}
            {image}
        </picture>
    )
}
